import { contructProductData } from "../../Layout/Common/algoliaInstantLib";

const MAX_ENTRIES = 300;
const entries = new Map();
const primedCards = new Map();

const safeDecode = (value) => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const previewKey = (pathname, search) => {
  const path = safeDecode(String(pathname ?? ""));
  const sku = new URLSearchParams(search ?? "").get("sku") ?? "";
  return `${path.toLowerCase()}|${sku.toLowerCase()}`;
};

const cardSignature = (hit) => `${hit?.key}|${hit?.sku}|${hit?.isfamily}|${hit?.price}|${hit?.familyId}`;

const formatFeature = (feature) => {
  const value = String(feature ?? "").trim();
  if (!value) return "";
  if (value.includes("</li><li>")) return `<ul><li>${value}</li></ul>`;
  const parts = value
    .split(/\r\n|\r|\n/)
    .map((part) => part.trim())
    .filter(Boolean);
  return `<ul><li>${parts.join("</li><li>")}</li></ul>`;
};

const stripTags = (value) =>
  String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .trim();

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const toBreadcrumb = (categoryPath) => {
  const path = Array.isArray(categoryPath) ? categoryPath[0] : categoryPath;
  if (!path || typeof path !== "string") return null;
  const names = path
    .split(">")
    .map((name) => name.trim())
    .filter(Boolean);
  return names.length ? names.map((itemValue) => ({ itemValue })) : null;
};

const familyForUrl = (item, pathname) => {
  const urlFamilyId = /-(\d+)\/?$/.exec(String(pathname ?? ""))?.[1];
  const match = urlFamilyId && (item.details ?? []).find((detail) => String(detail?.familyId) === urlFamilyId);
  return match ?? item.familyData ?? {};
};

const toFamilyImageUrl = (path) =>
  path ? process.env.REACT_APP_IMAGE_URL + String(path).replace(/\\/g, "/") : null;

const toPreview = (item, pathname, isFamily) => {
  const family = familyForUrl(item, pathname);
  const familyImage = toFamilyImageUrl(family.familyImage1) ?? item.prodImage;
  const images = isFamily ? [familyImage] : [item.imageUrl1, item.imageUrl2];
  const imageList = [...new Set(images.filter(Boolean))];
  const familyDetails = {
    familyId: String(family.familyId ?? item.familyId ?? ""),
    familyName: stripTags(family.familyName || item.productDescriptions),
    familyDesc: stripTags(family.descriptions),
    familyShortDesc: stripTags(family.shortDescription),
    feature: formatFeature(family.features),
    specification: String(family.specification ?? "").replace(/\r\n|\n/g, "<br />"),
    notes: "",
    applications: "",
    isFamily,
    iseBayBlock: String(item.eBayBlock).toLowerCase() === "true",
  };
  if (!isFamily) {
    Object.assign(familyDetails, {
      sku: String(item.sku ?? "").toLowerCase(),
      productId: String(item.productId ?? item.objectID ?? ""),
      productDescription: item.productDescriptions,
      productImage: imageList[0],
      productPrice: item.hidePrice ? null : toNumber(item.price),
    });
  }
  return {
    familyDetails,
    imageList,
    breadCrumb: toBreadcrumb(family.categoryPath ?? item.categoryPath),
    isPreview: true,
  };
};

const remember = (map, key, value) => {
  if (map.has(key)) map.delete(key);
  map.set(key, value);
  if (map.size > MAX_ENTRIES) map.delete(map.keys().next().value);
};

export const primeProductPreview = (hit) => {
  try {
    const signature = cardSignature(hit);
    const primedKey = primedCards.get(signature);
    if (primedKey && entries.has(primedKey)) return;
    const item = contructProductData(hit);
    if (!item?.link) return;
    const url = new URL(item.link, window.location.origin);
    const key = previewKey(url.pathname, url.search);
    remember(entries, key, item);
    remember(primedCards, signature, key);
  } catch {
    return;
  }
};

export const getProductPreview = (pathname, search) => {
  const item = entries.get(previewKey(pathname, search));
  if (!item) return null;
  try {
    return toPreview(item, pathname, !new URLSearchParams(search ?? "").get("sku"));
  } catch {
    return null;
  }
};
